import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useToast } from '../../components/ui/ToastProvider'
import { formatBusinessDateTime } from '../../lib/datetime'
import { useTableSort } from '../../lib/useTableSort'
import DashboardActionsCard from './components/DashboardActionsCard'
import ManagerDurationCard from './components/ManagerDurationCard'
import ManagerLoadCard from './components/ManagerLoadCard'
import PrioritizeTasksDialog from './components/PrioritizeTasksDialog'
import StatusBreakdownChart from './components/StatusBreakdownChart'
import StatusDistributionCard from './components/StatusDistributionCard'
import TopDelayedManagersCard from './components/TopDelayedManagersCard'
import ViewAllLink from './components/ViewAllLink'
import { viewAllHref } from './dashboardLinks'
import { useGrants } from '../../auth/useCurrentUser'
import { ACTION_LIST_ALL_SCOPE_GRANTS } from '../../auth/grants'
import { queryKeyParts } from './hooks/useActionDashboard'
import { useCanPrioritize } from './hooks/useCanPrioritize'
import { useActionReminder, useDashboardActions } from './hooks/useDashboardActions'
import {
  DEFAULT_OPEN_SELECTION,
  expandSelection,
  hasOpenSelection,
  parseSelection,
  toggleSelection,
  type ActionDashboard,
  type ActionSelection,
  type ActionSelectionKeys,
  type DashboardQuery,
  type PriorityCriteria,
  type ReminderResult,
  type StatusDistribution,
} from './types'

interface Props {
  data: ActionDashboard | undefined
  /** Etkin filtre — alt tablo ve hatırlatma aynı kümeyi hedeflesin diye kabuktan geçer. */
  query: DashboardQuery
}

/** Veri gelmeden önce bölümlere verilen boş dağılım (bileşenler boş-durum çizer). */
const EMPTY_DISTRIBUTION: StatusDistribution = { total: 0, slices: [] }

/**
 * Alt tablonun **açılıştaki** düzeni. 079'da "termin ↑" idi; **2026-09-11'de kullanıcı isteğiyle
 * "durum ↑" oldu**: satırlar önce ana duruma göre öbeklenir (Aksiyon Bekleniyor → Tamamlandı →
 * Kapatıldı), öbek içinde 079'un termin ↑ düzeni **korunur** (ikincil ölçüt sunucudadır).
 *
 * <p>Değer başlıktaki sıralama okunun açılışta doğru kolonda görünmesini de sağlar. Sunucunun
 * parametresiz varsayılanı (termin ↑) değişmedi — istemci sıralamayı her istekte açıkça gönderir.
 */
const DEFAULT_SORT = { sortField: 'status', sortOrder: 1 } as const

/**
 * **Açılış varsayılanına** yapılan ilk tıklamanın sonucu (kullanıcı kararı 2026-09-16): varsayılan seçim
 * kullanıcının değil, **sistemin** verdiği başlangıçtır → eklenmez, **yerine geçer**; aynı statüye
 * tıklandıysa kalkar (2026-09-11'in tek-seçim kuralı, olduğu gibi).
 *
 * <p>Burada da toggle uygulansaydı çoklu seçimin en sık iki ilk hareketi ters sonuç verirdi: panoyu açıp
 * "Tamamlandı"ya tıklayan kullanıcı **iki** ana durumu birden görürdü ve "Aksiyon Bekleniyor (Gecikmiş)"
 * sütununa tıklayan — grubu zaten seçili olduğu için — o kovayı seçimden **çıkarmış**, yani tam tersini
 * yapmış olurdu.
 */
function replaceDefault(current: ActionSelectionKeys, key: string): ActionSelection[] {
  const same = current.length === 1 && current[0] === key
  return same ? [] : parseSelection([key])
}

/**
 * **Yönetici Raporu** panosunun gövdesi (068 — analiz §1; 077 ve 079 ile revize).
 *
 * <p><b>079'da değişenler</b> (kullanıcı isteği 2026-08-07 + mockup):
 *
 * <ul>
 *   <li><b>Donut üç ana durum</b> gösterir (Devam Eden / Güncellenen / Tamamlanan) ve oranlar dilimlerin
 *       <b>üstünde</b> yazar.
 *   <li>Donut'un altına <b>"Durumlara Göre Detaylı Dağılım"</b> sütun grafiği eklendi: her ana durum
 *       zamanında/gecikmeli olarak ikiye ayrılır, sütun başına adet + oran yazılır.
 *   <li>Alt tablo <b>"Aksiyon Beklenen Maddeler"</b> oldu; kolonları sıralanabilir, varsayılan termin ↑.
 * </ul>
 *
 * <p><b>Tek seçim kümesi, iki grafik:</b> donut'taki grup da sütundaki kırılım da aynı `?bucket=`
 * parametresine yazılır ve alt tabloyu daraltır. Sunucu ikisini de çözdüğü için istemci "bu bir grup mu
 * kova mı" ayrımını taşımaz.
 *
 * <p><b>Çoklu seçim (kullanıcı isteği 2026-09-16):</b> `?bucket=` <b>tekrarlanabilir</b> — düz tıklama
 * seçime ekler, yeniden tıklamak çıkarır ve sunucu değerleri birleştirir (VEYA). Küme <b>tek</b>
 * yerdedir: ana durum seçimi kovalarına açılıp iki grafiğe de aynı kaynaktan dağıtılır, böylece donut ile
 * sütun grafiği ayrı seçim durumları tutmaz. Ana durum, kovalarının hepsi seçiliyse <b>tam</b>, bir kısmı
 * seçiliyse <b>kısmî</b> işaretlenir.
 *
 * <p>Filtre çubuğu ve "Son Güncelleme" üst kabukta ({@code HomeDashboard}) durur — iki pano onları
 * paylaşır.
 */
export default function ManagerReport({ data, query }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  const [searchParams, setSearchParams] = useSearchParams()
  /**
   * Kullanıcı seçimi **kaldırdı** mı? Açılış varsayılanı (aşağıda) yalnız bu olana kadar uygulanır —
   * aksi hâlde seçili dilime tıklamak hiçbir şey yapmayan ölü bir kontrol olurdu: seçim kalkar,
   * varsayılan onu aynı render'da geri getirirdi.
   */
  const [defaultCleared, setDefaultCleared] = useState(false)
  const rawBuckets = searchParams.getAll('bucket')
  /**
   * **Açılış seçimi** (kullanıcı isteği 2026-09-11): pano ilk açıldığında donut'ta **Aksiyon
   * Bekleniyor** seçili gelir ve alt tablo o kümeyi gösterir.
   *
   * <p>Varsayılan URL'ye **yazılmaz**, okuma tarafında uygulanır: mount'ta `setSearchParams` çağırmak
   * (ilk denenen yol) router'da kayboluyordu ve pano sessizce seçimsiz açılıyordu. Paylaşılan bağlantı
   * yine de aynı ekranı verir — parametresiz adres karşı tarafta da varsayılana düşer.
   *
   * <p>`?bucket=` **varsa** ona dokunulmaz; tanınmayan bir değerin seçimsiz duruma düşmesi §K3'te
   * tanımlı davranıştır (bozuk bağlantı panoyu boş bırakmamalı).
   */
  const openedWithDefault = rawBuckets.length === 0 && !defaultCleared
  const selection = openedWithDefault ? DEFAULT_OPEN_SELECTION : parseSelection(rawBuckets)
  /**
   * Alt tabloyu daraltan **kişi seçimi** (kullanıcı isteği 2026-09-11): "Yönetici Bazlı …" grafiğinde
   * ada tıklanınca en alttaki liste yalnız o kişinin görevlerini gösterir. Kova seçimiyle aynı desen —
   * URL'de durur, böylece yenileme/paylaşım seçimi kaybetmez.
   *
   * <p>Değer BURADA doğrulanmaz (kovanın aksine): geçerli siciller sunucudan gelen `personLoad`
   * satırlarıdır ve o veri henüz yüklenmemiş olabilir. Doğrulama, veri geldiğinde aşağıdaki etki ile
   * yapılır; sunucu da kapsamla kesişim aldığı için tanınmayan bir sicil veri açmaz.
   */
  const personId = searchParams.get('person')
  const [page, setPage] = useState(0)

  // Sıralama değişince sayfa BAŞA döner: 3. sayfadaki kayıt yeni düzende 1. sayfaya düşebilir.
  const resetPage = useCallback(() => setPage(0), [])
  const sorting = useTableSort(resetPage, DEFAULT_SORT)

  // Filtre (kişi/dönem) değişince sayfa BAŞA döner. Aksi hâlde 3. sayfadayken dönem daraltıldığında
  // sunucudan boş bir sayfa gelir ve kullanıcı tablonun boşaldığını sanır — oysa veri 1. sayfadadır.
  // Kova değişimi `select` içinde ayrıca sıfırlanır (o URL'den geldiği için burada görünmez).
  const filterKey = queryKeyParts(query).join('|')
  useEffect(() => {
    setPage(0)
  }, [filterKey])

  /**
   * "Görevleri Önceliklendir" açık mı? Tamamlanmış/kapatılmış bir işi hatırlatmak alıcıya yanlış
   * sinyaldir (§K6/SC-009) → o seçimlerde buton pasiftir. **Seçimsiz** hâlde buton AÇIKTIR: tablo tüm
   * statüleri gösterse de sunucu kümeyi ayrıca **açık** kayıtlarla sınırlar, yani kapalı bir işe bildirim
   * üretilmez.
   */
  const remindable = hasOpenSelection(selection)

  /**
   * Alt tablonun (ve toplu hatırlatmanın) sorgusu: panonun sorgusu + **kişi seçimi**. Seçim yalnız
   * buraya uygulanır — donut, kişi grafiği ve süre kartları panonun kendi sorgusundan beslenmeye devam
   * eder, çünkü kullanıcı bir kişiye tıkladığında grafiklerin ondan ibaret kalmasını değil, listenin
   * daralmasını ister (istek 2026-09-11). Kişi süzgeci `employeeIds` olarak gider; sunucu onu kapsamla
   * KESİŞTİRİR, ayrı bir uç/parametre gerekmez.
   */
  const tableQuery: DashboardQuery =
    personId == null ? query : { ...query, employeeIds: [personId] }

  const actions = useDashboardActions(tableQuery, selection, page, sorting.sort)
  const reminder = useActionReminder()
  const canPrioritize = useCanPrioritize()
  /** "Görevleri Önceliklendir" penceresi açık mı? (2026-10-21 — buton artık doğrudan göndermez) */
  const [prioritizeOpen, setPrioritizeOpen] = useState(false)

  const groups = data?.statusGroups ?? EMPTY_DISTRIBUTION
  const breakdown = data?.statusDistribution ?? EMPTY_DISTRIBUTION
  /**
   * Ana durum adları sözlüğü — donut ile Yönetici Bazlı kartın **tek kaynağı**. Sunucu dilimleri
   * katalogdan çözülmüş `statusName` ile gönderir; kart artık kendi sabit metnini taşımaz.
   */
  // Kaynak `statusGroups`tır, `statusDistribution` DEĞİL: kartın anahtarları GROUP_* (ana durum) iken
  // kırılım dilimlerinin `bucket`'ı PENDING/PENDING_LATE gibi alt kovalardır — sözlük ondan kurulursa
  // hiçbir anahtar eşleşmez ve kart SESSİZCE yedek metne düşer (ilk denemede tam olarak bu oldu:
  // sunucu "Aksiyon Bekleniyor" derken başlıkta i18n yedeği "Görev Bekleniyor" görünüyordu).
  // `statusName` sözleşmede null olabilir (eski/eşlenemeyen kova) — null adları sözlüğe ALMAYIZ ki
  // kart yedek metne düşebilsin; alsaydık legend/başlıkta boş bir alan görünürdü.
  const groupNames: Record<string, string> = Object.fromEntries(
    groups.slices
      .filter((s): s is typeof s & { statusName: string } => !!s.statusName)
      .map((s) => [s.bucket, s.statusName]),
  )

  // Seçimin adları donut/sütun grafiğiyle AYNI kaynaktan (sunucu) okunur — istemci ikinci bir etiket
  // eşlemesi tutmaz (§K8). Grup ve kırılım adları iki ayrı listede olduğu için ikisine de bakılır; sıra da
  // sunucunun dilim sırasıdır (önce ana durumlar, sonra kovalar), istemci kendi sırasını dayatmaz.
  // Seçim yokken liste BOŞ kalır: o hâlde tablo tüm statüleri gösterir ve açıklama bir statü adı yazmaz.
  const selectedKeys: readonly string[] = selection
  const bucketNames = [...groups.slices, ...breakdown.slices]
    .filter((s) => selectedKeys.includes(s.bucket))
    .map((s) => s.statusName)
    .filter((name): name is string => name != null)

  /**
   * Seçimin **kova** karşılığı — sütun grafiğinin okuduğu küme. Ana durum anahtarı burada kovalarına
   * açılır (eşleme SUNUCUDAN: `slice.group`), böylece grafik "bu bir grup mu kova mı" sorusunu hiç sormaz.
   */
  const chosen = expandSelection(selection, breakdown.slices)

  /**
   * Donut dilimlerinin (ana durumların) seçim hâli. Donut'un dilimleri ANA DURUMDUR, seçim ise tek bir
   * kırılım olabilir: o kova hangi gruba bağlıysa o dilim işaretlenir — aksi hâlde hiçbir dilim eşleşmez ve
   * DonutChart üçünü birden soluklaştırırdı (grafik "devre dışı" görünürdü).
   *
   * <p>Çoklu seçimle birlikte iki hâl ayrışır: kovalarının **hepsi** seçili olan grup <b>tam</b>,
   * **bir kısmı** seçili olan <b>kısmî</b>dir. Ayrım olmasaydı "Tamamlandı'nın tamamı" ile "yalnız
   * gecikmiş yarısı" donut'ta birebir aynı görünür, kullanıcı hangisini seçtiğini grafikten okuyamazdı.
   */
  const fullGroups: string[] = []
  const partialGroups: string[] = []
  for (const group of groups.slices) {
    const members = breakdown.slices.filter((s) => s.group === group.bucket)
    const hit = members.filter((s) => chosen.has(s.bucket))
    if (hit.length === 0) continue
    if (hit.length === members.length) fullGroups.push(group.bucket)
    else partialGroups.push(group.bucket)
  }

  /** Seçili kişinin grafikteki satırı — adı (nottaki metin) buradan okunur; yoksa `null`. */
  const personRow = data?.personLoad.find((p) => p.employeeId === personId) ?? null

  // Kişi seçimi grafikte GÖRÜNEN satırlardan yapılır; üstteki kişi/dönem filtresi değişince seçilen
  // kişi grafikten düşebilir — o hâlde alt tablo, ekranda karşılığı olmayan bir kişiye göre daralmış
  // kalırdı (kullanıcı listeyi neden boş gördüğünü anlayamaz). Veri geldiğinde seçim doğrulanır.
  useEffect(() => {
    if (personId == null || data == null || personRow != null) return
    const next = new URLSearchParams(searchParams)
    next.delete('person')
    setSearchParams(next, { replace: true })
    setPage(0)
  }, [personId, data, personRow, searchParams, setSearchParams])

  const selectPerson = (employeeId: string) => {
    const next = new URLSearchParams(searchParams)
    if (personId === employeeId) {
      next.delete('person') // aynı kişiye yeniden tıklamak seçimi KALDIRIR (kova seçimiyle aynı kural)
    } else {
      next.set('person', employeeId)
    }
    setSearchParams(next, { replace: true })
    setPage(0) // daralan kümenin 3. sayfasında açılmak anlamsız olurdu
  }

  /**
   * Bir statüye tıklandı — seçime **ekler ya da çıkarır** (§K3; 2026-09-16'dan beri çoklu). Tek istisna
   * açılış varsayılanıdır: oradaki ilk tıklama toggle etmez, yerine geçer ({@link replaceDefault}).
   *
   * <p>Küme URL'e `?bucket=` parametresinin **tekrarıyla** yazılır; `set` değil `append` kullanılır,
   * çünkü `set` bir önceki değeri ezip çoklu seçimi tek değere indirirdi.
   */
  const select = (key: string) => {
    const selected = openedWithDefault
      ? replaceDefault(selection, key)
      : toggleSelection(selection, key, breakdown.slices)
    const next = new URLSearchParams(searchParams)
    next.delete('bucket')
    selected.forEach((value) => next.append('bucket', value))
    if (selected.length === 0) {
      // Seçim boşaldı → açılış varsayılanı da kalkar: aksi hâlde silinen parametrenin yerine varsayılan
      // geri gelir ve kullanıcının son tıklaması hiçbir şey yapmamış görünürdü.
      setDefaultCleared(true)
    }
    setSearchParams(next, { replace: true })
    setPage(0) // yeni filtrenin 3. sayfasında açılmak anlamsız olurdu
  }

  /**
   * Hatırlatma sonucunu kullanıcıya çevirir. Durumlar ayrı ayrı söylenir; "gönderildi" deyip geçmek,
   * hiç satır üretilmediği (şablon/alıcı yok) ya da gönderimin **sessiz saat** nedeniyle ötelendiği
   * hâlde başarı bildirmek olurdu.
   *
   * <p>Öteleme notu üst sınır uyarısına da **eklenir** (ikisi birlikte olabilir: gece yapılan toplu
   * çağrı hem kesilmiş hem ötelenmiş olabilir); ayrı bir toast açmak yerine tek mesajda birleşir.
   */
  const reportResult = (result: ReminderResult) => {
    if (result.notificationCount === 0) {
      toast.warn(t('home.remind.none'))
      return
    }
    const counts = { actions: result.actionCount, notifications: result.notificationCount }
    // Saat KURUMSAL dilimde basılır: `deferredUntil` sunucunun kural gereği ürettiği bir andır (gönderim
    // saati sistem parametresinden gelir) ve tarayıcı yerel saatinde gösterilirse mesaj kendi söylediği
    // pencerenin içinde bir saat vaat eder.
    const quietNote = result.deferredUntil
      ? ` ${t('home.remind.quietHours', { time: formatBusinessDateTime(result.deferredUntil) })}`
      : ''
    if (result.totalCount > result.actionCount) {
      // Ötelenmişken "gönderildi" diyen metin, hemen ardından gelen "gönderilmez" notuyla çelişirdi →
      // üst sınır uyarısının da kuyruğa-alındı varyantı var.
      const partial = t(quietNote ? 'home.remind.partialQueued' : 'home.remind.partial', {
        sent: result.actionCount,
        total: result.totalCount,
      })
      toast.warn(`${partial}${quietNote}`)
      return
    }
    if (quietNote) {
      toast.warn(`${t('home.remind.queued', counts)}${quietNote}`)
      return
    }
    toast.success(t('home.remind.success', counts))
  }

  const remindOne = (actionId: number) =>
    reminder.single.mutate(actionId, { onSuccess: reportResult })

  /**
   * Toplu önceliklendirme (2026-10-21): pencerede seçilen ölçütler, ekranda GÖRÜLEN kümeyi (dönem · kişi
   * · statü — 077 §K6) **daraltır**, yerine geçmez. Pencere yalnız başarıda kapanır; hata merkezî
   * huniden bildirilir ve kullanıcı seçimini kaybetmeden yeniden deneyebilir.
   *
   * <p><b>Ölçüte uyan görev yoksa</b> (`totalCount === 0`) bu ayrı söylenir ve pencere **açık kalır**:
   * daraltma "0 eşleşme"yi sıradan bir sonuç yaptı, oysa `reportResult`'ın `notificationCount === 0`
   * dalı onu "atanan/şablon yok" diye bir yapılandırma hatası gibi anlatırdı. Kullanıcı ölçütü
   * genişletip yeniden gönderebilir.
   */
  const remindAll = (criteria: PriorityCriteria) =>
    reminder.bulk.mutate(
      { query: tableQuery, selection, criteria },
      {
        onSuccess: (result) => {
          if (result.totalCount === 0) {
            toast.warn(t('home.prioritize.noMatch'))
            return
          }
          setPrioritizeOpen(false)
          reportResult(result)
        },
      },
    )

  /**
   * Donut kartının **"Tümünü Gör"** bağlantısı (kullanıcı bildirimi 2026-08-31: "Görev Durum
   * Dağılımı"nda buton yoktu — Raporum'un aynı kartında vardı).
   *
   * <p><b>Hedef "Tüm Görevler"dir:</b> bu panoyu gören üç rol "Tüm Görevler"i açar — Özel Kalem
   * Müdürü/Personeli `ACTION_LIST_ALL_VIEW`, Genel Müdür 06.10.2026'dan beri sınırlı hâlini
   * (`ACTION_LIST_ALL_LIMITED_VIEW`). <b>Bilinen sapma (kabul edildi):</b> Genel Müdür'de kart,
   * Başkan ve üzerinin kendi ekipleri için açtığı doğrudan görevleri de sayabilir (pano kapsamı
   * değişmedi — kullanıcı kararı), bağlantının açtığı liste ise onları göstermez. Yetki yine de ARANIR (Raporum'daki desen): pano sunucu-kapsamıyla
   * dolar, liste yetkisi ondan bağımsızdır ve yetkisiz kullanıcıya bağlantı gösterilseydi rota
   * `RequireGrant`'i "yetkiniz yok" ekranına düşerdi.
   *
   * <p><b>Kişi süzgeci yalnız TEK kişi seçiliyken taşınır</b> (`&mainResponsible=`): hedef listenin
   * <b>Ana Sorumlu</b> süzgeci tek sicil alır. Çoklu seçimde bağlantı kişiyi taşımaz — panoya göre daha
   * geniş bir liste açar; kartın kümesini birebir veren bir süzgeç listede yok.
   *
   * <p><b>Anahtar 2026-09-15'te değişti</b> (`responsible` → `mainResponsible`): listedeki tek kişi
   * süzgeci ikiye ayrıldı (Ana Sorumlu · Süreç Destek Yöneticisi) ve eski anahtar artık hiç okunmuyor —
   * güncellenmeseydi bağlantı süzgeci <b>sessizce</b> düşürür, kullanıcı tek kişi yerine kapsamdaki
   * herkesin görevlerini görürdü.
   *
   * <p><b>Bilinen sınır (daraldı):</b> kart kişinin görevlerini <b>role bakmadan</b> sayar
   * (`MinutesAssignee.employeeId IN :employeeIds` — rol koşulu yok, rolsüz eski kayıtlar dahil), oysa
   * bağlantı yalnız <b>Ana Sorumlu</b> kutusunu doldurur. Kişi bir görevde Süreç Destek Yöneticisi ise o
   * satır kartta sayılır ama listede görünmez. Eskiden süzgeç Ana VEYA Yan'a bakıyordu, yani sapma
   * vardı ama daha küçüktü. Kartın kümesini birebir veren bir süzgeç hâlâ yok.
   *
   * <p>Seçili statü (donut dilimi / sütun kırılımı) bilinçli olarak TAŞINMAZ: kova → statü kimliği
   * eşlemesi yalnız sunucuda vardır ve istemci ikinci bir eşleme tutmaz (§K8). Raporum'un statü
   * donut'undaki bağlantı da aynı sebeple kapsamın tamamını açar.
   */
  const statusViewAll = () => {
    if (!ACTION_LIST_ALL_SCOPE_GRANTS.some((g) => hasGrant(g))) return undefined
    const person = query.employeeIds.length === 1 ? `&mainResponsible=${query.employeeIds[0]}` : ''
    return <ViewAllLink to={viewAllHref('all', query, person)} />
  }

  return (
    <>
      <div className="home-grid home-grid--manager">
        <StatusDistributionCard
          distribution={groups}
          title={t('home.distribution.title')}
          hint={statusViewAll()}
          selectedBuckets={fullGroups}
          partialBuckets={partialGroups}
          onSelect={select}
          footNote={t('home.distribution.clickHint')}
          origin={data?.taskOrigin}
        >
          <StatusBreakdownChart
            breakdown={breakdown}
            groups={groups}
            selectedBuckets={[...chosen]}
            onSelect={select}
          />
        </StatusDistributionCard>
        <ManagerLoadCard
          people={data?.personLoad ?? []}
          groupNames={groupNames}
          selectedPerson={personId}
          onSelectPerson={selectPerson}
        />
      </div>

      {/* İkinci satır (086): solda gecikme sıralaması, sağda ortalama süre — ikisi de kişi bazlıdır ve
          üstteki çubuk grafiğin iki farklı okumasıdır, bu yüzden onun hemen altında dururlar.
          Bölüşüm 1/3 — 2/3'tür (`--thirds`, kullanıcı isteği 2026-08-17): sol kart üç satır, sağdaki
          tablo 10 yöneticiye kadar çıkar. */}
      <div className="home-grid home-grid--thirds">
        <TopDelayedManagersCard rows={data?.topDelayedManagers ?? []} />
        <ManagerDurationCard rows={data?.managerDurations ?? []} />
      </div>

      <DashboardActionsCard
        rows={actions.data?.content ?? []}
        loading={actions.isLoading}
        total={actions.data?.page.totalElements ?? 0}
        page={page}
        onPageChange={setPage}
        sorting={sorting}
        selectedSelection={selection}
        remindable={remindable}
        bucketNames={bucketNames}
        openedWithDefault={openedWithDefault}
        personName={personRow?.fullName ?? null}
        onRemind={canPrioritize ? (row) => remindOne(row.id) : undefined}
        onRemindAll={canPrioritize ? () => setPrioritizeOpen(true) : undefined}
        reminding={reminder.single.isPending || reminder.bulk.isPending}
      />

      {canPrioritize && (
        <PrioritizeTasksDialog
          visible={prioritizeOpen}
          submitting={reminder.bulk.isPending}
          onSubmit={remindAll}
          onHide={() => setPrioritizeOpen(false)}
        />
      )}
    </>
  )
}
